import type { DsmScopeItem } from "design-scope";

/** Same label the Design Package column valueGetter exposes to the set filter. */
export function designPackageColumnValue(
  item: DsmScopeItem,
  nameOfDp: (id: string) => string
): string {
  const c = item.commitments[0];
  return c?.designPackageId ? nameOfDp(c.designPackageId) : "";
}

/** Same label the Bid Package column valueGetter exposes to the set filter. */
export function bidPackageColumnValue(
  item: DsmScopeItem,
  nameOfBp: (id: string) => string
): string {
  const b = item.bids.find((x) => x.primary) ?? item.bids[0];
  return b?.bidPackageId ? nameOfBp(b.bidPackageId) : "";
}

export function uniqueNonEmptySorted(values: Iterable<string>): string[] {
  return [...new Set([...values].filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );
}

export const DESIGN_PACKAGE_COL_ID = "designPackage";
export const BID_PACKAGE_COL_ID = "bidPackage";

/** Shown in the log when a scope item has no linked design package. */
export const EMPTY_DESIGN_PACKAGE_LABEL = "No Design Package";

/** Shown in the log when a scope item has no linked bid package. */
export const EMPTY_BID_PACKAGE_LABEL = "No Bid Package";

const EMPTY_PACKAGE_EXPORT_LABELS = new Set<string>([
  EMPTY_DESIGN_PACKAGE_LABEL,
  EMPTY_BID_PACKAGE_LABEL
]);

/**
 * CSV and Excel should leave the empty-package tags blank. The log still
 * renders those labels in the cell.
 */
export function blankEmptyPackageLabelForExport(
  colId: string | undefined,
  value: unknown
): unknown {
  if (
    (colId === DESIGN_PACKAGE_COL_ID || colId === BID_PACKAGE_COL_ID) &&
    typeof value === "string" &&
    EMPTY_PACKAGE_EXPORT_LABELS.has(value)
  ) {
    return "";
  }
  return value;
}

type PackageNameFns = {
  nameOfDp?: (id: string) => string;
  nameOfBp?: (id: string) => string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function packageNameFns(context: unknown): PackageNameFns {
  if (!isRecord(context)) return {};
  const source = isRecord(context.entities) ? context.entities : context;
  return {
    nameOfDp:
      typeof source.nameOfDp === "function"
        ? (source.nameOfDp as (id: string) => string)
        : undefined,
    nameOfBp:
      typeof source.nameOfBp === "function"
        ? (source.nameOfBp as (id: string) => string)
        : undefined
  };
}

function namedIds(
  data: unknown,
  field: "commitments" | "bids",
  idKey: "designPackageId" | "bidPackageId"
): { id: string; primary: boolean }[] | undefined {
  if (!isRecord(data) || !Array.isArray(data[field])) return undefined;
  return data[field].flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const id = entry[idKey];
    if (typeof id !== "string" || !id) return [];
    return [{ id, primary: entry.primary === true }];
  });
}

/** Primary link first, then the rest in list order. */
function primaryFirst(
  links: { id: string; primary: boolean }[]
): { id: string; primary: boolean }[] {
  const primaryIndex = links.findIndex((link) => link.primary);
  if (primaryIndex <= 0) return links;
  const primary = links[primaryIndex];
  return [
    primary,
    ...links.slice(0, primaryIndex),
    ...links.slice(primaryIndex + 1)
  ];
}

/**
 * Bid packages stay one cell, one name per line. Design packages use this
 * join only when the caller is not splitting each link onto its own row.
 */
export function designScopeLinkedPackagesForExport(args: {
  colId: string | undefined;
  value: unknown;
  data: unknown;
  context: unknown;
}): unknown {
  const { nameOfDp, nameOfBp } = packageNameFns(args.context);
  const joinNames = (
    links: { id: string; primary: boolean }[] | undefined,
    nameOf: ((id: string) => string) | undefined,
    orderPrimaryFirst: boolean
  ): string | undefined => {
    if (!links || !nameOf) return undefined;
    const ordered = orderPrimaryFirst ? primaryFirst(links) : links;
    return ordered
      .map((link) => nameOf(link.id).trim())
      .filter(Boolean)
      .join("\n");
  };

  if (args.colId === DESIGN_PACKAGE_COL_ID) {
    const names = joinNames(
      namedIds(args.data, "commitments", "designPackageId"),
      nameOfDp,
      false
    );
    if (names !== undefined) return names;
  }

  if (args.colId === BID_PACKAGE_COL_ID) {
    const names = joinNames(
      namedIds(args.data, "bids", "bidPackageId"),
      nameOfBp,
      true
    );
    if (names !== undefined) return names;
  }

  return blankEmptyPackageLabelForExport(args.colId, args.value);
}

const MILESTONE_COL_ID = "milestone";
const LOD_COL_ID = "lod";

type ExportCommitment = {
  designPackageId?: string;
  milestoneId?: string | null;
  lod?: number | null;
};

function commitmentsOf(data: unknown): ExportCommitment[] | undefined {
  if (!isRecord(data) || !Array.isArray(data.commitments)) return undefined;
  return data.commitments.filter(isRecord);
}

function nameOfMilestone(
  context: unknown
): ((id: string) => string) | undefined {
  if (!isRecord(context)) return undefined;
  const source = isRecord(context.entities) ? context.entities : context;
  return typeof source.nameOfMilestone === "function"
    ? (source.nameOfMilestone as (id: string) => string)
    : undefined;
}

/**
 * CSV keeps only the first design package on the scope item row. Further
 * links are separate rows from designScopeCsvDesignPackageContinuationRows.
 * Returns undefined when this row is not a design-scope item.
 */
export function designScopeCsvFirstDesignPackage(args: {
  data: unknown;
  context: unknown;
}): string | undefined {
  const commitments = commitmentsOf(args.data);
  const { nameOfDp } = packageNameFns(args.context);
  if (!commitments || !nameOfDp) return undefined;
  const id = commitments[0]?.designPackageId;
  if (!id) return "";
  return nameOfDp(id).trim();
}

/**
 * One CSV row per design package after the first. Only Design Package,
 * Milestone, and LOD are filled; every other exported column stays blank.
 */
export function designScopeCsvDesignPackageContinuationRows(args: {
  data: unknown;
  context: unknown;
  columnIds: readonly string[];
}): string[][] {
  const commitments = commitmentsOf(args.data);
  const { nameOfDp } = packageNameFns(args.context);
  if (!commitments || !nameOfDp || commitments.length < 2) return [];
  const milestoneName = nameOfMilestone(args.context);
  return commitments.slice(1).flatMap((commitment) => {
    const id = commitment.designPackageId;
    if (!id) return [];
    const cells = args.columnIds.map(() => "");
    const write = (colId: string, value: string) => {
      const index = args.columnIds.indexOf(colId);
      if (index >= 0 && value) cells[index] = value;
    };
    write(DESIGN_PACKAGE_COL_ID, nameOfDp(id).trim());
    if (commitment.milestoneId && milestoneName) {
      write(MILESTONE_COL_ID, milestoneName(commitment.milestoneId).trim());
    }
    if (commitment.lod != null) write(LOD_COL_ID, `L${commitment.lod}`);
    return [cells];
  });
}

type SetFilterModel = { filterType?: string; values?: string[] | null };

/** Read set-filter values for a column from an AG Grid filter model. */
export function setFilterValuesFromModel(
  filterModel: Record<string, unknown> | null | undefined,
  colId: string
): string[] {
  const entry = filterModel?.[colId] as SetFilterModel | null | undefined;
  if (!entry?.values?.length) return [];
  return entry.values.filter((v): v is string => typeof v === "string");
}

/** Merge one column's set-filter values into an existing filter model. */
export function withSetFilterValues(
  filterModel: Record<string, unknown> | null | undefined,
  colId: string,
  values: string[]
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(filterModel ?? {}) };
  if (values.length === 0) {
    delete next[colId];
  } else {
    next[colId] = { filterType: "set", values };
  }
  return next;
}

export function sameStringSets(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((left, right) => left.localeCompare(right));
  const sortedB = [...b].sort((left, right) => left.localeCompare(right));
  return sortedA.every((v, i) => v === sortedB[i]);
}
